import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { X, Search, Loader } from 'lucide-react';
import './LinkedInConnectionsImport.css';

interface LinkedInConnection {
  fullName: string;
  company?: string;
  title?: string;
  profileUrl: string;
  location?: string;
  email?: string;
}

interface LinkedInConnectionsImportProps {
  onComplete: (skipped?: boolean) => void;
}

type Step = 'instructions' | 'exporting' | 'selecting' | 'importing';

const LinkedInConnectionsImport: React.FC<LinkedInConnectionsImportProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('instructions');
  const [cookieValue, setCookieValue] = useState('');
  const [containerId, setContainerId] = useState<string | null>(null);
  const [connections, setConnections] = useState<LinkedInConnection[]>([]);
  const [selectedConnections, setSelectedConnections] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pollingTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Cleanup polling timers on unmount
  React.useEffect(() => {
    return () => {
      clearPollingTimer();
    };
  }, []);

  // Handle cookie submission
  const handleSubmitCookie = async () => {
    if (!cookieValue.trim()) {
      setError('Please paste your LinkedIn cookie');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Save the cookie
      await apiService.saveLinkedInCookie(cookieValue.trim());

      console.log('✅ LinkedIn cookie saved, starting export...');

      // Start the connections export
      setStep('exporting');
      const user = await apiService.getCurrentUser();
      const linkedinProfileUrl = user.linkedinProfileUrl || 'https://www.linkedin.com/in/temp';

      const response = await apiService.exportLinkedInConnections(linkedinProfileUrl);
      setContainerId(response.containerId);

      // Start polling for export results
      pollExportStatus(response.containerId);
    } catch (err: any) {
      console.error('Error submitting cookie:', err);
      setError(err.response?.data?.error || 'Failed to save LinkedIn cookie. Please try again.');
      setLoading(false);
    }
  };

  // Clear polling timer
  const clearPollingTimer = () => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
      console.log('🛑 Cleared polling timer');
    }
  };

  // Poll export status until finished
  const pollExportStatus = async (cid: string) => {
    const maxAttempts = 120; // 10 minutes max (5 second intervals)
    let attempts = 0;
    
    // Clear any errors when starting to poll
    setError('');

    const poll = async () => {
      try {
        const status = await apiService.checkLinkedInExportStatus(cid);

        console.log('📊 Poll status response:', status);
        
        // Clear error if we successfully got a status (even if running)
        // This prevents temporary errors from persisting
        if (status.status === 'running' || status.status === 'finished') {
          setError('');
        }

        if (status.status === 'finished') {
          // Always show results, even if empty or all duplicates
          // Duplicate checking happens during import, not here
          const connectionsToShow = status.connections || [];
          setConnections(connectionsToShow);
          setStep('selecting');
          setLoading(false);
          
          if (connectionsToShow.length === 0) {
            console.warn('Export finished but no connections returned');
          } else {
            console.log(`✅ Showing ${connectionsToShow.length} connections (duplicates will be skipped during import)`);
          }
          return;
        } else if (status.status === 'error') {
          const errorMsg = status.error || status.message || 'Export failed. Please try again.';
          console.error('Export error:', errorMsg);
          setError(errorMsg);
          setStep('instructions');
          setLoading(false);
          return;
        } else if (status.status === 'running') {
          // Still running, continue polling
          console.log(`⏳ Export still running... (attempt ${attempts + 1}/${maxAttempts})`);
        }

        attempts++;
        if (attempts < maxAttempts) {
          pollingTimerRef.current = setTimeout(poll, 5000); // Poll every 5 seconds
        } else {
          setError('Export timed out after 10 minutes. The export may still be processing. Please check back later.');
          setStep('instructions');
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Error polling status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          // Continue polling on temporary errors - don't set error state
          // This prevents UI from showing "failed" when it's just a temporary network issue
          console.warn(`⚠️ Temporary error, continuing to poll (attempt ${attempts}/${maxAttempts}):`, err.message);
          // Don't set error here - keep polling
          pollingTimerRef.current = setTimeout(poll, 5000);
        } else {
          // Only set error if we've exhausted all attempts
          const errorMsg = err.response?.data?.error || err.message || 'Failed to check export status after multiple attempts. The export may still be processing.';
          setError(errorMsg);
          setStep('instructions');
          setLoading(false);
        }
      }
    };

    poll();
  };

  // Toggle connection selection
  const toggleConnection = (profileUrl: string) => {
    const newSelected = new Set(selectedConnections);
    if (newSelected.has(profileUrl)) {
      newSelected.delete(profileUrl);
    } else {
      newSelected.add(profileUrl);
    }
    setSelectedConnections(newSelected);
  };

  // Select all / deselect all
  const toggleSelectAll = () => {
    if (selectedConnections.size === filteredConnections.length) {
      setSelectedConnections(new Set());
    } else {
      setSelectedConnections(new Set(filteredConnections.map(c => c.profileUrl)));
    }
  };

  // Filter connections based on search
  const filteredConnections = connections.filter(conn => {
    const search = searchTerm.toLowerCase();
    return (
      conn.fullName.toLowerCase().includes(search) ||
      conn.company?.toLowerCase().includes(search) ||
      conn.title?.toLowerCase().includes(search) ||
      conn.location?.toLowerCase().includes(search)
    );
  });

  // Handle skip
  const handleSkip = async () => {
    clearPollingTimer();
    localStorage.setItem('linkedInImportDismissed', 'true');
    onComplete(true); // Pass true to indicate it was skipped
  };

  // Handle import
  const handleImport = async () => {
    if (selectedConnections.size === 0) {
      setError('Please select at least one person to import');
      return;
    }

    setLoading(true);
    setStep('importing');
    setError('');

    try {
      const connectionsToImport = connections.filter(c => selectedConnections.has(c.profileUrl));
      const response = await apiService.importLinkedInConnections(connectionsToImport);

      if (response.success) {
        // Import successful, clear dismissal flag and proceed
        localStorage.removeItem('linkedInImportDismissed');
        clearPollingTimer();
        onComplete(false);
      } else {
        setError('Failed to import connections. Please try again.');
        setStep('selecting');
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to import connections. Please try again.');
      setStep('selecting');
      setLoading(false);
    }
  };

  // Render step 1: Instructions for copying LinkedIn cookie
  if (step === 'instructions') {
    return (
      <div className="linkedin-import-container">
        <button
          className="linkedin-import-close-button"
          onClick={handleSkip}
          title="Close"
        >
          <X size={24} />
        </button>
        <div className="linkedin-import-cards-wrapper">
          <div className="linkedin-import-card linkedin-import-card-left">
            <div className="linkedin-import-header">
              <h1>Import your LinkedIn connections</h1>
              <p>Our LinkedIn integration is in beta, so it's a bit more manual for now.</p>
            </div>

            <div style={{ marginTop: '10px', textAlign: 'left' }}>
              <h3 style={{ marginBottom: '10px', fontSize: '14px', fontWeight: 600 }}>How to get your LinkedIn cookie:</h3>
              <ol style={{ lineHeight: '1.5', paddingLeft: '20px', fontSize: '13px', color: '#4b5563', margin: 0 }}>
                <li>Open <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" style={{ color: '#0a66c2', textDecoration: 'underline' }}>LinkedIn.com</a>, log in, and press <strong>F12</strong> to open Developer Tools</li>
                <li>Go to <strong>Application</strong> tab → <strong>Cookies</strong> → <strong>linkedin.com</strong> and find the <strong>li_at</strong> cookie</li>
                <li>Copy the <strong>Value</strong> and paste it below</li>
              </ol>

              <div style={{ marginTop: '1.5rem' }}>
                <textarea
                  value={cookieValue}
                  onChange={(e) => setCookieValue(e.target.value)}
                  placeholder="Paste your li_at cookie value here..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    resize: 'vertical'
                  }}
                  disabled={loading}
                />
              </div>
            </div>

            {error && <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>}

            <div className="button-group" style={{ marginTop: '2rem' }}>
              <button
                type="button"
                className="nav-button submit-button"
                onClick={handleSubmitCookie}
                disabled={loading || !cookieValue.trim()}
              >
                {loading ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render step 2: Exporting (loading state)
  if (step === 'exporting') {
    return (
      <div className="linkedin-import-container">
        <button 
          className="linkedin-import-close-button"
          onClick={handleSkip}
          title="Close"
        >
          <X size={24} />
        </button>
        <div className="linkedin-import-content">
          <div className="exporting-state">
            <Loader className="spinner" size={48} />
            <h2>Finding Your LinkedIn Connections</h2>
            <p>This may take a few minutes. Please don't close this page.</p>
            {/* Don't show errors during exporting - they're likely temporary */}
          </div>
        </div>
      </div>
    );
  }

  // Render step 3: Connection selection
  if (step === 'selecting') {
    return (
      <div className="linkedin-import-container">
        <button 
          className="linkedin-import-close-button"
          onClick={handleSkip}
          title="Close"
        >
          <X size={24} />
        </button>
        <div className="linkedin-import-content">
          <div className="linkedin-import-header">
            <h1>Select People to Import</h1>
            <p>Choose which LinkedIn connections you'd like to add to your CRM</p>
          </div>

          <div className="connections-toolbar">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search people..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="selection-info">
              <span>{selectedConnections.size} of {connections.length} selected</span>
              <button 
                type="button" 
                className="link-button"
                onClick={toggleSelectAll}
              >
                {selectedConnections.size === filteredConnections.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="connections-table-container">
            <table className="connections-table">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedConnections.size === filteredConnections.length && filteredConnections.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Title</th>
                  <th>Location</th>
                  <th>LinkedIn</th>
                </tr>
              </thead>
              <tbody>
                {filteredConnections.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="no-results">
                      {searchTerm ? 'No people match your search' : 'No people found'}
                    </td>
                  </tr>
                ) : (
                  filteredConnections.map((connection, index) => (
                    <tr key={connection.profileUrl || index} className={selectedConnections.has(connection.profileUrl) ? 'selected' : ''}>
                      <td className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={selectedConnections.has(connection.profileUrl)}
                          onChange={() => toggleConnection(connection.profileUrl)}
                        />
                      </td>
                      <td className="name-col">{connection.fullName}</td>
                      <td>{connection.company || '-'}</td>
                      <td>{connection.title || '-'}</td>
                      <td>{connection.location || '-'}</td>
                      <td>
                        <a 
                          href={connection.profileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="profile-link"
                        >
                          View Profile
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="button-group">
            <button 
              type="button" 
              className="nav-button secondary-button"
              onClick={handleSkip}
              disabled={loading}
            >
              Skip for now
            </button>
            <button 
              type="button" 
              className="nav-button submit-button"
              onClick={handleImport}
              disabled={loading || selectedConnections.size === 0}
            >
              {loading ? 'Importing...' : `Import ${selectedConnections.size} Person${selectedConnections.size !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render step 4: Importing (loading state)
  if (step === 'importing') {
    return (
      <div className="linkedin-import-container">
        <button 
          className="linkedin-import-close-button"
          onClick={handleSkip}
          title="Close"
        >
          <X size={24} />
        </button>
        <div className="linkedin-import-content">
          <div className="exporting-state">
            <Loader className="spinner" size={48} />
            <h2>Importing People</h2>
            <p>Adding {selectedConnections.size} people to your CRM...</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default LinkedInConnectionsImport;

